import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../axiosConfig';
import DrillLogMapPreview from './DrillLogMapPreview';

const UploadPage = ({ showToast }) => {
  const navigate = useNavigate();

  // 파일 업로드
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(null); // {stage, done, total}

  // 좌표 설정
  const [drillLogData, setDrillLogData] = useState(null); // 서버에서 받은 데이터
  const [drillLogName, setDrillLogName] = useState(''); // 편집 가능한 시추주상도 이름
  const [coordinateSystem, setCoordinateSystem] = useState('new'); // 디폴트: 신좌표
  const [epsgCode, setEpsgCode] = useState('EPSG:5181'); // 디폴트: 중부원점
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false); // 좌표계 변경 시 재조회 중

  // 파일 선택 핸들러
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
    } else {
      showToast('PDF 파일만 선택할 수 있습니다.');
      setSelectedFile(null);
    }
  };

  // 파일 업로드 및 미리보기
  const handleUpload = async () => {
    if (!selectedFile) {
      showToast('파일을 선택해주세요.');
      return;
    }

    setIsUploading(true);
    setProgress(null);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('name', selectedFile.name.replace(/\.[^/.]+$/, ''));
    formData.append('coordinate_system', coordinateSystem);
    formData.append('epsg_code', epsgCode);

    try {
      // SSE 스트리밍 preview: 시추공별 진행률을 받으며 변환
      const token = localStorage.getItem('token');
      const response = await fetch(`${axios.defaults.baseURL}/drill_logs/preview-stream`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!response.ok || !response.body) throw new Error('서버 연결에 실패했습니다.');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let resultData = null;
      let errorMsg = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf('\n')) >= 0) {
          const line = buffer.slice(0, idx).replace(/\r$/, '');
          buffer = buffer.slice(idx + 1);
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data.startsWith('[RESULT] ')) {
            try { resultData = JSON.parse(data.slice(9)); } catch (e) { errorMsg = '결과 파싱 오류'; }
          } else if (data.startsWith('[ERROR]')) {
            errorMsg = data.slice(7).trim() || '변환 중 오류가 발생했습니다.';
          } else if (data.startsWith('[COMPLETE]')) {
            // 완료
          } else if (data.trim()) {
            try { setProgress(JSON.parse(data)); } catch (e) { /* 무시 */ }
          }
        }
      }

      if (errorMsg) {
        showToast(errorMsg);
      } else if (resultData) {
        setDrillLogData(resultData);
        setDrillLogName(resultData.name);
        showToast('파일 분석 완료! 아래에서 좌표계를 확인하고 수정하세요.');
      } else {
        showToast('분석 결과를 받지 못했습니다.');
      }
    } catch (error) {
      console.error('업로드 실패:', error);
      showToast(error.message || '업로드 중 오류가 발생했습니다.');
    } finally {
      setIsUploading(false);
      setProgress(null);
    }
  };

  // 좌표계 변경 시 서버에 다시 요청
  const handleCoordinateSystemChange = async (newSystem) => {
    setCoordinateSystem(newSystem);
    if (selectedFile && drillLogData) {
      await refreshPreview(newSystem, epsgCode);
    }
  };

  const handleEpsgCodeChange = async (newEpsgCode) => {
    setEpsgCode(newEpsgCode);
    if (selectedFile && drillLogData) {
      await refreshPreview(coordinateSystem, newEpsgCode);
    }
  };

  // 좌표계 변경 후 미리보기 갱신 (컨버터 재실행 없이 좌표만 재변환 — 빠르고 비용 없음)
  const refreshPreview = async (system, epsg) => {
    if (!drillLogData) return;
    setIsRefreshing(true);
    try {
      const response = await axios.post('/drill_logs/retransform', {
        boreholes: drillLogData.boreholes,
        coordinate_system: system,
        epsg_code: epsg,
      });
      setDrillLogData(prev => ({ ...prev, boreholes: response.data.data.boreholes }));
      showToast('좌표가 다시 계산되었습니다.');
    } catch (error) {
      console.error('좌표 갱신 실패:', error);
      showToast('좌표 갱신에 실패했습니다.');
    } finally {
      setIsRefreshing(false);
    }
  };

  // 저장 (좌표 확정 및 DB 저장)
  const handleSave = async () => {
    if (!drillLogName || drillLogName.trim() === '') {
      showToast('시추주상도 이름을 입력해주세요.');
      return;
    }

    setIsSaving(true);

    try {
      // confirm API로 DB 저장
      const confirmData = {
        name: drillLogName,
        pdf_file_checksum: drillLogData.pdf_file_checksum,
        borehole_count: drillLogData.borehole_count,
        boreholes: drillLogData.boreholes.map(borehole => ({
          name: borehole.name,
          x: borehole.x,
          y: borehole.y,
          coordinate_system: coordinateSystem,
          epsg_code: epsgCode,
          ground_water_table: borehole.ground_water_table,
          spts: borehole.spts
        }))
      };

      const response = await axios.post('/drill_logs/confirm', confirmData);

      showToast('시추주상도가 저장되었습니다!');

      // 상세 페이지로 이동
      setTimeout(() => {
        navigate(`/drill-logs/${response.data.data.id}`);
      }, 500);
    } catch (error) {
      console.error('저장 실패:', error);
      const errorMessage = error.response?.data?.message || '저장 중 오류가 발생했습니다.';
      showToast(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-secondary-50">
      <div className="max-w-7xl mx-auto p-8 space-y-6">
        {/* 헤더 */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">신규 데이터 업로드</h1>
          <p className="text-secondary-600">
            시추주상도 PDF 파일을 업로드하고 좌표계를 설정하세요
          </p>
        </div>

          {/* Step 1: 파일 업로드 */}
          <div className="bg-white rounded-2xl shadow-medium p-8">
            <h2 className="text-2xl font-bold text-secondary-900 mb-6 flex items-center">
              <span className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                1
              </span>
              파일 업로드
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  PDF 파일 선택 <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-secondary-500 file:mr-4 file:py-3 file:px-6 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 file:cursor-pointer"
                  disabled={isUploading || drillLogData !== null}
                />
                {selectedFile && (
                  <p className="mt-2 text-sm text-secondary-600">
                    선택된 파일: <span className="font-medium text-secondary-900">{selectedFile.name}</span>
                  </p>
                )}
              </div>

              <button
                onClick={handleUpload}
                disabled={!selectedFile || isUploading || drillLogData !== null}
                className={`w-full px-6 py-4 rounded-xl font-semibold text-white transition-all duration-200 ${
                  !selectedFile || isUploading || drillLogData !== null
                    ? 'bg-secondary-300 cursor-not-allowed'
                    : 'bg-gradient-to-r from-primary-600 to-primary-700 hover:shadow-hard'
                }`}
              >
                {isUploading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    분석 중...
                  </span>
                ) : drillLogData !== null ? (
                  '✓ 업로드 완료'
                ) : (
                  '파일 업로드 및 분석'
                )}
              </button>

              {isUploading && (() => {
                const total = progress?.total || 0;
                const done = progress?.done || 0;
                const pct = total > 0 ? Math.round((done / total) * 100) : null;
                const label = !progress
                  ? 'PDF 업로드 중...'
                  : progress.stage === 'segmented'
                    ? `시추공 ${total}개 발견 — 데이터 추출 시작...`
                    : `시추공 데이터 추출 중... (${done}/${total})`;
                return (
                  <div className="mt-2 p-4 bg-primary-50 rounded-xl border border-primary-100">
                    <div className="flex justify-between items-center text-sm mb-2">
                      <span className="font-medium text-secondary-700">{label}</span>
                      {pct !== null && (
                        <span className="font-bold text-primary-700 tabular-nums">{pct}%</span>
                      )}
                    </div>
                    <div className="w-full h-2.5 bg-secondary-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full bg-gradient-to-r from-primary-500 to-primary-700 rounded-full transition-all duration-300 ${
                          pct === null ? 'w-1/3 animate-pulse' : ''
                        }`}
                        style={pct !== null ? { width: `${pct}%` } : undefined}
                      />
                    </div>
                    <p className="mt-2 text-xs text-secondary-500">
                      큰 파일(수백 페이지)은 시추공 수에 따라 수 분이 걸릴 수 있습니다.
                    </p>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Step 2: 좌표 설정 (파일 업로드 후 표시) */}
          {drillLogData && (
            <div className="bg-white rounded-2xl shadow-medium p-8 animate-fade-in">
              <h2 className="text-2xl font-bold text-secondary-900 mb-6 flex items-center">
                <span className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                  2
                </span>
                좌표계 설정 및 미리보기
              </h2>

              {/* 시추주상도 이름 편집 */}
              <div className="mb-6 p-6 bg-secondary-50 rounded-xl">
                <label className="block text-sm font-semibold text-secondary-700 mb-3">
                  시추주상도 이름 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={drillLogName}
                  onChange={(e) => setDrillLogName(e.target.value)}
                  className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-lg font-medium"
                  placeholder="시추주상도 이름 입력"
                />
                <p className="mt-2 text-sm text-secondary-600">
                  시추공 개수: <span className="font-semibold text-secondary-900">{drillLogData.borehole_count}개</span>
                </p>
              </div>

              {/* 좌표계 설정 */}
              <div className="mb-6 p-6 bg-secondary-50 rounded-xl">
                <h3 className="text-lg font-bold text-secondary-900 mb-4">좌표계 설정</h3>

                <div className="space-y-4">
                  {/* 좌표 시스템 */}
                  <div>
                    <label className="text-sm font-medium text-secondary-700 mb-2 block">
                      좌표 시스템 <span className="text-red-500">*</span>
                    </label>
                    <div className="flex space-x-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="coordinateSystem"
                          value="new"
                          checked={coordinateSystem === 'new'}
                          onChange={() => handleCoordinateSystemChange('new')}
                          disabled={isRefreshing}
                          className="w-4 h-4 text-primary-600"
                        />
                        <span className="text-sm text-secondary-700">신좌표 (200,000mE, 600,000mN)</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="coordinateSystem"
                          value="old"
                          checked={coordinateSystem === 'old'}
                          onChange={() => handleCoordinateSystemChange('old')}
                          disabled={isRefreshing}
                          className="w-4 h-4 text-primary-600"
                        />
                        <span className="text-sm text-secondary-700">구좌표 (200,000mE, 500,000mN)</span>
                      </label>
                    </div>
                  </div>

                  {/* 원점 선택 */}
                  <div>
                    <label className="text-sm font-medium text-secondary-700 mb-2 block">
                      원점 선택 <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={epsgCode}
                      onChange={(e) => handleEpsgCodeChange(e.target.value)}
                      disabled={isRefreshing}
                      className="w-full px-4 py-2 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="EPSG:5181">중부</option>
                      <option value="EPSG:5185">서부</option>
                      <option value="EPSG:5187">동부</option>
                      <option value="EPSG:5188">동해</option>
                    </select>
                  </div>

                  {isRefreshing && (
                    <div className="flex items-center text-sm text-primary-600">
                      <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      좌표 재계산 중...
                    </div>
                  )}
                </div>
              </div>

              {/* 좌표 정보 표 */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-secondary-900 mb-3">시추공 좌표 미리보기</h3>
                <div className="overflow-x-auto rounded-xl border border-secondary-200">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary-100 border-b border-secondary-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-secondary-700">시추공</th>
                        <th className="px-4 py-3 text-right font-semibold text-secondary-700">X</th>
                        <th className="px-4 py-3 text-right font-semibold text-secondary-700">Y</th>
                        <th className="px-4 py-3 text-right font-semibold text-secondary-700">위도 (GRS80)</th>
                        <th className="px-4 py-3 text-right font-semibold text-secondary-700">경도 (GRS80)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-100">
                      {drillLogData.boreholes.map((borehole, index) => (
                        <tr key={index} className="hover:bg-secondary-50">
                          <td className="px-4 py-3 font-medium text-secondary-900">
                            <div className="flex items-center gap-2">
                              <span>{borehole.name}</span>
                              {borehole.geocoded && (
                                <span className="text-xs font-semibold text-primary-700 bg-primary-50 px-2 py-0.5 rounded-full">
                                  📍 주소 자동
                                </span>
                              )}
                            </div>
                            {borehole.address && (
                              <div className="text-xs text-secondary-500 font-normal mt-0.5">{borehole.address}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right text-secondary-900">{borehole.x.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right text-secondary-900">{borehole.y.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right text-secondary-900">
                            {borehole.grs80_x ? borehole.grs80_x.toFixed(6) : 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-right text-secondary-900">
                            {borehole.grs80_y ? borehole.grs80_y.toFixed(6) : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 지도 미리보기 */}
              {drillLogData.boreholes.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-secondary-900 mb-3">지도 미리보기</h3>
                  <div className="rounded-xl overflow-hidden border border-secondary-200">
                    <DrillLogMapPreview boreholes={drillLogData.boreholes} />
                  </div>
                </div>
              )}

              {/* 저장 버튼 */}
              <div className="flex space-x-4">
                <button
                  onClick={handleSave}
                  disabled={isSaving || !drillLogName || isRefreshing}
                  className={`flex-1 px-6 py-4 rounded-xl font-semibold text-white transition-all duration-200 ${
                    isSaving || !drillLogName || isRefreshing
                      ? 'bg-secondary-300 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-600 to-green-700 hover:shadow-hard'
                  }`}
                >
                  {isSaving ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      저장 중...
                    </span>
                  ) : (
                    '저장 및 완료'
                  )}
                </button>
                <button
                  onClick={() => {
                    setDrillLogData(null);
                    setDrillLogName('');
                    setSelectedFile(null);
                    setCoordinateSystem('new');
                    setEpsgCode('EPSG:5181');
                    showToast('업로드를 취소했습니다.');
                  }}
                  disabled={isSaving || isRefreshing}
                  className="px-6 py-4 rounded-xl font-semibold bg-secondary-200 text-secondary-700 hover:bg-secondary-300 transition-colors duration-200"
                >
                  취소
                </button>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default UploadPage;
